import { useState, useCallback } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X, AlertCircle } from 'lucide-react'
import { Spinner } from '@/components/ui'
import { useVoiceBrowser } from '@/hooks/useVoiceBrowser'
import { VoiceCard } from './VoiceCard'
import { VoiceFilters } from './VoiceFilters'
import { VoicePreviewPlayer } from './VoicePreviewPlayer'
import type { BrowsableVoice } from './types'

interface VoiceBrowserModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  provider: string
  hasApiKey: boolean
  onSelect: (voice: BrowsableVoice) => void
}

export function VoiceBrowserModal({
  open,
  onOpenChange,
  provider,
  hasApiKey,
  onSelect,
}: VoiceBrowserModalProps) {
  const [activeTab, setActiveTab] = useState<'user' | 'library'>('user')
  const [lastPlayedVoice, setLastPlayedVoice] = useState<BrowsableVoice | null>(null)

  const browser = useVoiceBrowser(provider, open && hasApiKey)

  const handlePlay = useCallback(
    (voice: BrowsableVoice) => {
      setLastPlayedVoice(voice)
      browser.playPreview(voice)
    },
    [browser]
  )

  const handleSelect = useCallback(
    (voice: BrowsableVoice) => {
      browser.stopPreview()
      onSelect(voice)
      onOpenChange(false)
    },
    [browser, onSelect, onOpenChange]
  )

  const voices = activeTab === 'user' ? browser.userVoices : browser.libraryVoices
  const isLoading = activeTab === 'user' ? browser.userVoicesLoading : browser.libraryLoading
  const error = activeTab === 'user' ? browser.userVoicesError : browser.libraryError
  const showLibraryTab = browser.capabilities?.supports_library ?? false

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-background border border-border rounded-xl shadow-2xl z-50 w-[95vw] max-w-[800px] max-h-[80vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <Dialog.Title className="text-lg font-semibold text-foreground">
              Browse Voices
            </Dialog.Title>
            <Dialog.Close className="text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </Dialog.Close>
          </div>

          {/* No API key state */}
          {!hasApiKey && provider !== 'kokoro' ? (
            <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
              <AlertCircle className="w-10 h-10 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-1">
                To browse voices, add your {provider === 'elevenlabs' ? 'ElevenLabs' : provider} API key in Settings.
              </p>
              <a
                href="/settings"
                className="text-sm text-primary hover:text-primary/80 underline"
                onClick={() => onOpenChange(false)}
              >
                Go to Settings
              </a>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="flex border-b border-border px-5">
                <button
                  onClick={() => setActiveTab('user')}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'user'
                      ? 'text-primary border-primary'
                      : 'text-muted-foreground border-transparent hover:text-foreground'
                  }`}
                >
                  My Voices
                </button>
                {showLibraryTab && (
                  <button
                    onClick={() => setActiveTab('library')}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'library'
                        ? 'text-primary border-primary'
                        : 'text-muted-foreground border-transparent hover:text-foreground'
                    }`}
                  >
                    Voice Library
                  </button>
                )}
              </div>

              {/* Filters */}
              <VoiceFilters
                filters={activeTab === 'library' ? browser.filters : { search: browser.userSearch }}
                onFiltersChange={(f) => {
                  if (activeTab === 'library') {
                    browser.setFilters(f)
                  } else if (f.search !== undefined) {
                    browser.setUserSearch(f.search ?? '')
                  }
                }}
                onClear={() => {
                  if (activeTab === 'library') {
                    browser.clearFilters()
                  } else {
                    browser.setUserSearch('')
                  }
                }}
                showDropdowns={activeTab === 'library'}
              />

              {/* Voice list */}
              <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2 min-h-0">
                {isLoading && voices.length === 0 && (
                  <div className="flex items-center justify-center py-12">
                    <Spinner size="lg" />
                  </div>
                )}

                {error && (
                  <div className="flex flex-col items-center py-8 text-center">
                    <AlertCircle className="w-8 h-8 text-destructive mb-2" />
                    <p className="text-sm text-destructive">
                      {error.message.includes('401') || error.message.includes('403')
                        ? 'Your API key appears to be invalid. Please check it in Settings.'
                        : "Couldn't reach the voice provider. Try again in a moment."}
                    </p>
                  </div>
                )}

                {!isLoading && !error && voices.length === 0 && (
                  <div className="flex flex-col items-center py-12 text-center">
                    <p className="text-sm text-muted-foreground">No voices match your filters</p>
                    <button
                      onClick={() => {
                        if (activeTab === 'library') browser.clearFilters()
                        else browser.setUserSearch('')
                      }}
                      className="text-xs text-primary hover:text-primary/80 mt-2"
                    >
                      Clear filters
                    </button>
                  </div>
                )}

                {voices.map((voice) => (
                  <VoiceCard
                    key={voice.voice_id}
                    voice={voice}
                    isPlaying={browser.playingVoiceId === voice.voice_id}
                    onPlay={() => handlePlay(voice)}
                    onSelect={() => handleSelect(voice)}
                  />
                ))}

                {/* Load more (library tab only) */}
                {activeTab === 'library' && browser.hasMore && (
                  <div className="flex justify-center pt-2 pb-1">
                    <button
                      onClick={browser.loadMore}
                      disabled={browser.libraryLoading}
                      className="text-sm text-primary hover:text-primary/80 disabled:opacity-50"
                    >
                      {browser.libraryLoading ? 'Loading...' : 'Load more'}
                    </button>
                  </div>
                )}
              </div>

              {/* Custom text preview bar */}
              <VoicePreviewPlayer
                voiceId={lastPlayedVoice?.voice_id ?? null}
                voiceName={lastPlayedVoice?.name ?? null}
                onSynthesize={browser.synthesizeCustom}
                isSynthesizing={browser.isSynthesizing}
                supportsPreviewSynthesis={browser.capabilities?.supports_preview_synthesis ?? false}
              />

              {/* Footer */}
              <div className="flex items-center justify-between px-5 py-3 border-t border-border">
                <span className="text-xs text-muted-foreground">
                  {activeTab === 'library' && browser.totalCount != null
                    ? `Showing ${voices.length} of ${browser.totalCount.toLocaleString()} voices`
                    : `${voices.length} voice${voices.length !== 1 ? 's' : ''}`}
                </span>
                <Dialog.Close className="text-xs text-muted-foreground hover:text-foreground border border-border px-3 py-1.5 rounded-md">
                  Cancel
                </Dialog.Close>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
